/**
 * HealthStatusChart 组件
 *
 * 使用 Recharts 显示搜索供应商健康状态分布图。
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { SearchProviderStats } from '../../../services/searchAdminApi';

interface HealthStatusChartProps {
  providers: SearchProviderStats[];
  loading: boolean;
}

const COLORS = {
  healthy: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  disabled: '#9ca3af',
};

export const HealthStatusChart: React.FC<HealthStatusChartProps> = ({
  providers,
  loading,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-48 animate-pulse bg-gray-100 rounded" />
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        暂无健康数据
      </div>
    );
  }

  const chartData = providers.map((p) => {
    const hasOpenCircuit = p.circuit_breakers.some(
      (cb) => cb.state === 'open'
    );
    let status: 'healthy' | 'warning' | 'error' | 'disabled' = 'healthy';
    if (!p.enabled) status = 'disabled';
    else if (hasOpenCircuit || p.total_failures > 10) status = 'error';
    else if (p.total_failures > 3 || p.avg_latency_ms > 3000)
      status = 'warning';

    return {
      name: p.display_name || p.provider_name,
      checks: p.total_checks,
      failures: p.total_failures,
      latency: Math.round(p.avg_latency_ms),
      status,
    };
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-600 mb-4">
          供应商检查/失败统计
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
              <Bar dataKey="checks" name="检查次数" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="failures" name="失败次数" radius={[2, 2, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[entry.status]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-600 mb-4">
          平均延迟 (ms)
        </h3>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" unit="ms" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
              <Bar dataKey="latency" name="延迟(ms)" radius={[2, 2, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.latency > 3000
                        ? '#ef4444'
                        : entry.latency > 1000
                          ? '#f59e0b'
                          : '#10b981'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-green-500" /> 健康
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-yellow-500" /> 警告
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-500" /> 异常
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-gray-400" /> 禁用
        </span>
      </div>
    </div>
  );
};

export default HealthStatusChart;
